import { ApiError, request } from '@/api/client'
import { normalizeUploadDraft } from '@/api/packages'
import { mapVersionDetail } from '@/api/versionMapper'
import { listAllVersions } from '@/api/versions'
import { DEFAULT_APP_ICON } from '@/lib/appIcon'
import type { AppInfo, AppStatus, AppVersion, VersionStatus } from '@/types'

/** GET /developer/apps 查询参数（对齐 api (2).md） */
export interface ListAppsParams {
  keyword?: string
  member?: string
  /** 按最近一版状态过滤 */
  status?: VersionStatus
  page?: number
  page_size?: number
  /** 覆盖 x-org-id，用来探测其他空间是否已有应用 */
  orgId?: string
}

/** 列表项领域模型（页面再映射到卡片 UI） */
export interface AppListItem {
  /** 平台 AppID，用作路由 */
  id: string
  /** 开发者填写的模块 ID */
  module_id: string
  name: string
  icon_url?: string
  version_id?: string
  version_code?: string
  status?: VersionStatus
  owner_nickname?: string
  owner_username?: string
  space_id?: string
  space_name?: string
  has_collaborators: boolean
  updated_at?: string
}

export interface ListAppsResult {
  list: AppListItem[]
  page: number
  page_size: number
  total: number
}

/** 后端可能返回 snake_case 或 camelCase，两侧都读 */
interface AppListItemDto {
  app_id?: string
  appId?: string
  /** 模块 ID。新应用与 app_id 不同；旧数据缺省时与 app_id 相同 */
  id?: string
  module_id?: string
  moduleId?: string
  name?: string
  icon_url?: string
  iconUrl?: string
  version_id?: string
  versionId?: string
  version_code?: string
  versionCode?: string
  status?: VersionStatus
  owner?: {
    nickname?: string
    username?: string
    user_name?: string
    userName?: string
  }
  space?: { id?: string; name?: string; icon_url?: string; iconUrl?: string }
  has_collaborators?: boolean
  hasCollaborators?: boolean
  updated_at?: string
  updatedAt?: string
}

interface AppListDataDto {
  list?: AppListItemDto[]
  pagination?: {
    page?: number
    page_size?: number
    pageSize?: number
    total?: number
  }
}

function moduleIdOf(dto: {
  app_id?: string
  appId?: string
  id?: string
  module_id?: string
  moduleId?: string
}): string {
  const appId = dto.app_id ?? dto.appId ?? ''
  return dto.module_id ?? dto.moduleId ?? dto.id ?? appId
}

export function mapAppListItem(dto: AppListItemDto): AppListItem {
  const id = dto.app_id ?? dto.appId ?? ''
  return {
    id,
    module_id: moduleIdOf(dto),
    name: dto.name ?? id,
    icon_url: dto.icon_url ?? dto.iconUrl,
    version_id: dto.version_id ?? dto.versionId,
    version_code: dto.version_code ?? dto.versionCode,
    status: dto.status,
    owner_nickname: dto.owner?.nickname,
    owner_username:
      dto.owner?.user_name ?? dto.owner?.userName ?? dto.owner?.username,
    space_id: dto.space?.id,
    space_name: dto.space?.name,
    has_collaborators: dto.has_collaborators ?? dto.hasCollaborators ?? false,
    updated_at: dto.updated_at ?? dto.updatedAt,
  }
}

/**
 * 开发者控制台应用卡片列表。
 * keyword 搜应用名或 id；status 按最近一版接口状态过滤。
 */
export async function listApps(
  params: ListAppsParams = {},
): Promise<ListAppsResult> {
  const data = await request<AppListDataDto>('/developer/apps', {
    orgId: params.orgId,
    params: {
      keyword: params.keyword?.trim() || undefined,
      member: params.member || undefined,
      status: params.status || undefined,
      page: params.page ?? 1,
      page_size: params.page_size ?? 20,
    },
  })

  const pagination = data.pagination ?? {}
  return {
    list: (data.list ?? []).map(mapAppListItem),
    page: pagination.page ?? params.page ?? 1,
    page_size:
      pagination.page_size ?? pagination.pageSize ?? params.page_size ?? 20,
    total: pagination.total ?? 0,
  }
}

/** GET /developer/apps/:appId 响应（对齐 api (2).md） */
interface AppDetailDto {
  app_id?: string
  appId?: string
  /** 模块 ID。新应用与 app_id 不同；旧数据缺省时与 app_id 相同 */
  id?: string
  module_id?: string
  moduleId?: string
  name?: string
  icon_url?: string
  iconUrl?: string
  status?: string
  app_status?: number
  appStatus?: number
  created_at?: string
  createdAt?: string
  description_i18n?: Record<string, string>
  descriptionI18n?: Record<string, string>
  versions?: unknown[]
}

export interface AppDetailResult {
  app: AppInfo
  versions: AppVersion[]
  /** 为 false 时版本只是分页片段，调用方不得整表替换 */
  versionsComplete: boolean
}

function mapAppStatus(app_status?: number, status?: string): AppStatus {
  if (app_status === 1) return 'published'
  if (app_status === 2 || app_status === 3) return 'offline'
  if (status === 'released') return 'published'
  return 'development'
}

function pickDescription(
  i18n?: Record<string, string>,
): string {
  if (!i18n) return ''
  return i18n['zh-CN'] ?? i18n.en ?? Object.values(i18n)[0] ?? ''
}

/** 应用详情 DTO → 领域 AppInfo（不含 versions） */
export function mapAppDetail(dto: AppDetailDto): AppInfo {
  const id = dto.app_id ?? dto.appId ?? ''
  const created = dto.created_at ?? dto.createdAt ?? ''
  return {
    id,
    name: dto.name ?? id,
    description: pickDescription(dto.description_i18n ?? dto.descriptionI18n),
    icon: dto.icon_url ?? dto.iconUrl ?? DEFAULT_APP_ICON,
    app_id: id,
    module_id: moduleIdOf(dto),
    app_secret: 'sk_****************',
    status: mapAppStatus(
      dto.app_status ?? dto.appStatus,
      dto.status,
    ),
    created_at: created ? created.slice(0, 10) : new Date().toISOString().slice(0, 10),
  }
}

/** GET /developer/apps/:appId/secret 当前启用中的换票凭证（明文 app_secret） */
export interface AppSecret {
  id: string
  app_id: string
  app_secret: string
  identity_type?: string
  status?: number
  permission_packages?: string[]
  created_at?: string
  updated_at?: string
}

interface AppSecretDto {
  id?: string
  app_id?: string
  appId?: string
  app_secret?: string
  appSecret?: string
  identity_type?: string
  identityType?: string
  status?: number
  permission_packages?: string[]
  permissionPackages?: string[]
  created_at?: string
  createdAt?: string
  updated_at?: string
  updatedAt?: string
}

export function mapAppSecret(dto: AppSecretDto, fallbackAppId: string): AppSecret {
  return {
    id: dto.id ?? '',
    app_id: dto.app_id ?? dto.appId ?? fallbackAppId,
    app_secret: dto.app_secret ?? dto.appSecret ?? '',
    identity_type: dto.identity_type ?? dto.identityType,
    status: dto.status,
    permission_packages: dto.permission_packages ?? dto.permissionPackages,
    created_at: dto.created_at ?? dto.createdAt,
    updated_at: dto.updated_at ?? dto.updatedAt,
  }
}

export async function getAppSecret(appId: string): Promise<AppSecret> {
  const dto = await request<AppSecretDto>(`/developer/apps/${appId}/secret`)
  return mapAppSecret(dto, appId)
}

/** POST /developer/apps/:appId/secret/reset 重置后返回新的明文 app_secret */
export async function resetAppSecret(appId: string): Promise<AppSecret> {
  const dto = await request<AppSecretDto>(
    `/developer/apps/${appId}/secret/reset`,
    { method: 'POST', body: { appId } },
  )
  return mapAppSecret(dto, appId)
}

interface AppIconDto {
  icon_url?: string
  iconUrl?: string
  icon?: string
}

/** POST /developer/apps/:appId/icon multipart `file`，png/jpg/gif ≤ 2MB */
export async function uploadAppIcon(appId: string, file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const dto = await request<AppIconDto>(`/developer/apps/${appId}/icon`, {
    method: 'POST',
    body: form,
  })
  return dto?.icon_url ?? dto?.iconUrl ?? dto?.icon ?? ''
}

/** POST /developer/apps 创建应用。name 为平台展示名，id 为模块 ID */
export interface CreatedApp {
  app_id: string
  module_id: string
  name: string
  app_secret: string
}

interface CreatedAppDto {
  app_id?: string
  appId?: string
  id?: string
  module_id?: string
  moduleId?: string
  name?: string
  app_secret?: string
  appSecret?: string
}

export function mapCreatedApp(
  dto: CreatedAppDto,
  requested: { name: string; id: string },
): CreatedApp {
  return {
    app_id: dto.app_id ?? dto.appId ?? '',
    module_id: dto.module_id ?? dto.moduleId ?? dto.id ?? requested.id,
    name: dto.name ?? requested.name,
    app_secret: dto.app_secret ?? dto.appSecret ?? '',
  }
}

export async function createApp(input: {
  name: string
  id: string
}): Promise<CreatedApp> {
  const name = input.name.trim()
  const id = input.id.trim()
  const dto = await request<CreatedAppDto>('/developer/apps', {
    method: 'POST',
    body: { name, id },
  })
  const created = mapCreatedApp(dto ?? {}, { name, id })
  if (!created.app_id) {
    throw new ApiError(-1, 'Invalid create app response')
  }
  return created
}

/** PATCH /developer/apps/:appId 修改应用名称，最长 128 字 */
export async function updateAppName(appId: string, name: string): Promise<string> {
  const dto = await request<{ name?: string }>(`/developer/apps/${appId}`, {
    method: 'PATCH',
    body: { name },
  })
  return dto?.name ?? name
}

/** 应用详情：应用信息 + 嵌套 versions（未必全量，见 versionsComplete） */
export async function getApp(app_id: string): Promise<AppDetailResult> {
  const raw = await request<AppDetailDto>(`/developer/apps/${app_id}`)
  const versions = (raw.versions ?? []).map((item) =>
    mapVersionDetail(normalizeUploadDraft(item, { app_id })),
  )
  return {
    app: mapAppDetail(raw),
    versions,
    // 嵌套 versions 不一定全量；bootstrap / 刷新须 listAllVersions 再 replace
    versionsComplete: false,
  }
}

function listItemToAppInfo(item: AppListItem): AppInfo {
  return {
    id: item.id,
    name: item.name || item.id,
    description: '',
    icon: item.icon_url || DEFAULT_APP_ICON,
    app_id: item.id,
    module_id: item.module_id,
    app_secret: 'sk_****************',
    status: item.status === 'released' ? 'published' : 'development',
    created_at: item.updated_at?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
  }
}

/**
 * 只要应用身份（名称、图标、模块 ID）。
 * 只 map 应用字段，不解析嵌套 versions；失败时降级为列表里的同一条应用。
 */
export async function loadAppIdentity(app_id: string): Promise<AppInfo> {
  try {
    const raw = await request<AppDetailDto>(`/developer/apps/${app_id}`)
    return mapAppDetail(raw)
  } catch (detailErr) {
    const listed = await listApps({ keyword: app_id, page: 1, page_size: 50 })
    const item = listed.list.find((a) => a.id === app_id)
    if (!item) throw detailErr
    return listItemToAppInfo(item)
  }
}

/**
 * 详情页整份快照：优先 GET 应用详情；嵌套 versions 不全时翻页拉全量。
 * 后端未部署详情路由时降级为列表项 + 版本列表。
 * 进详情页的名称用 loadAppIdentity；版本列表由侧栏 / refreshAppVersions 拉。
 */
export async function loadAppForDetail(app_id: string): Promise<AppDetailResult> {
  try {
    const detail = await getApp(app_id)
    if (detail.versionsComplete) return detail
    const all = await listAllVersions(app_id)
    return {
      ...detail,
      versions: all.list,
      versionsComplete: true,
    }
  } catch (detailErr) {
    const listed = await listApps({ keyword: app_id, page: 1, page_size: 50 })
    const item = listed.list.find((a) => a.id === app_id)
    if (!item) throw detailErr

    const versions = await listAllVersions(app_id)
    return {
      app: listItemToAppInfo(item),
      versions: versions.list,
      versionsComplete: true,
    }
  }
}
